import React, { useCallback, useEffect, useState } from "react"
import { addToCart, setCart } from "@/store/features/cartSlice"
import { showPreviewCart } from "@/store/features/genericSlice"
import styles from "@/components/ProductDetail/v3/ProductDetailView/PDPCuratedProducts/pdpCuratedProduct.module.scss"
import Button from "@/components/core/Button/Button"
import ParentProductCard from "@/components/Shared/UpsellInstallServices/ParentProductCard"
import RequiredProductItem from "@/components/Shared/UpsellInstallServices/RequiredProductItem"
import useWindowResize from "@/hooks/useWindowResize"
import { getPresetUrl, getRowCols } from "@/utils/index"
import { isMobile, isTablet } from "react-device-detect"
import {
  getUserPersona,
  isShowAnalyticsEventLoaded,
  rafThrottle,
} from "@/utils/helper"
import { getConfig } from "@/constants/config"
import {
  getListOfRequiredAdobeTargetSkus,
  getProductDataFromSkuIds,
} from "@/components/Shared/UpsellInstallServices/api"
import { getProductInventory } from "@/services/productServices"
import _isEmpty from "lodash/isEmpty"
import { selectPdpState } from "@/store/features/pdpSlice"
import { useDispatch, useSelector } from "react-redux"
import DisplayDiscountPrice from "@/components/Shared/DisplayDiscountPrice"
import { addToCartItem } from "@/utils/addTocart"
import {
  addAnalyticsPDPWhenUpSellVisible,
  addAnalyticsForPdpAddToCart,
  addAnalyticsForIncludeforRequiredItem,
} from "@/components/Shared/UpsellInstallServices/analytics"
import { getTntId } from "@/utils/ApiUtil"
import { createContextLogger } from "@/utils/logger/client"

const PDPCuratedProducts = props => {
  const {
    authData = {},
    quickViewAuthData = {},
    openQuickViewModal = () => {},
  } = props || {}

  const {
    presetConfigs = {},
    recommendationTitle = "",
    selectedTitle = "",
    swatchUrl: baseUrl = "",
    mainTitle = "",
    productTitle = "",
  } = authData

  const logger = createContextLogger("pdp:curated-products")
  const dispatch = useDispatch()
  const [width] = useWindowResize()
  const {
    pdp: { sku: updatedSku = "" } = {},
    pdp: PdpInfo = {},
    requiredItemsSkuData: { data: requiredItemsSkuData = [] },
  } = useSelector(selectPdpState)
  const persona = getUserPersona()
  const rowCols = getRowCols(width, isMobile, isTablet)
  const [parentProductData, setParentProductData] = useState({})
  const [requiredItemsProductData, setRequiredItemsProductData] = useState([])
  const [loading, setLoading] = useState(true)
  const [includedItems, setIncludedItems] = useState({})
  const [brandName, setBrandName] = useState("kohler")
  const [addToCartLoader, setAddToCartLoader] = useState(false)
  const [recommendationSource, setRecommendationSource] = useState("n/a")
  const [criteriaTitle, setCriteriaTitle] = useState("")

  const parsedPresetConfigs = !_isEmpty(presetConfigs)
    ? JSON.parse(presetConfigs)
    : {}

  const handleScroll = () => {
    const scrollpostion = document.querySelector(
      ".pdp-curated-recommendation"
    )?.offsetTop

    const yscrollposition = window.scrollY

    if (yscrollposition >= scrollpostion - 300) {
      if (!window.isUpsellAnalaticsLoaded && isShowAnalyticsEventLoaded()) {
        const productDataForAnalytics = anylticsPdpData()
        if (recommendationSource !== "n/a") {
          addAnalyticsPDPWhenUpSellVisible({
            recommendation: recommendationTitle,
            productDataForAnalytics,
            persona,
            recommendationSource,
          })
          window.isUpsellAnalaticsLoaded = true
        }
      }
    }
  }

  const onScroll = useCallback(() => {
    handleScroll()
  }, [parentProductData, requiredItemsProductData])

  useEffect(() => {
    if (typeof document !== "undefined") {
      // Coalesce the offsetTop/scrollY read into one animation frame per scroll
      // and register passively to avoid forcing layout on every scroll event.
      const throttledScroll = rafThrottle(onScroll)
      document.addEventListener("scroll", throttledScroll, { passive: true })
      return () => {
        document.removeEventListener("scroll", throttledScroll)
      }
    }
  }, [onScroll])

  const requiredItemsSkuDataString = JSON.stringify(requiredItemsSkuData)

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.isUpsellAnalaticsLoaded = false
      if (requiredItemsSkuDataString) {
        executeInitialCalls()
      }
    }
  }, [requiredItemsSkuDataString])

  const anylticsPdpData = () => {
    const parentProductDataInfo = PdpInfo.data ?? []
    const { ["Color.SKU.Details_ss"]: colors = [] } = parentProductDataInfo
    let matchedColor = []
    if (colors.length) {
      matchedColor = colors.filter(colorItem => {
        const colorSplit = colorItem.split("|")
        const itemLen = colorSplit.length
        return (
          colorSplit[itemLen - 1].substring(
            2,
            colorSplit[itemLen - 1].length
          ) === PdpInfo.sku
        )
      })
    }

    const parentProductDataAll = {
      desc: parentProductDataInfo?.ProductDescriptionProductShort_s,
      category: parentProductDataInfo?.ProductLocalCategory_s,
      image: parentProductData?.productImageUrl,
      unitPrice: parentProductData?.unitPrice,
      productPrice: parentProductData?.unitPrice,
      customerFacingSKU: parentProductData?.customerFacingSKU,
      productTileName: parentProductData?.name,
      quantity: 1,
      name: parentProductData?.name,
      color: matchedColor ? matchedColor[0]?.split("|")[0] : "",
      parentglobalPromotionPrice:
        PdpInfo.data?.[`priceList.${persona}.finalPrice_d`],
      [`priceList.${persona}.discountedPrice_d`]:
        PdpInfo.data?.[`priceList.${persona}.finalPrice_d`],
      itemType: "product",
      productRoomType: parentProductDataInfo?.category1_s,
    }
    const productDataForAnlytics = {
      parentProductDataAll,
      requiredItemsProductData,
    }
    return productDataForAnlytics ?? {}
  }
  const executeInitialCalls = async () => {
    const config = await getConfig()
    const { general: { siteName = "", lwAppName = "" } = {} } = config ?? {}

    if (!requiredItemsSkuData.length) {
      setRequiredItemsProductData([])
      setLoading(false)
      return
    }

    const { inventories = [] } = await getProductInventory(updatedSku)
    const parentProductQuantity = inventories.length
      ? inventories[0]?.availableQuantity
      : 0

    const {
      CrossSellRecommendationSKUList_ss: requiredSkuList = [],
      ProductBrandName_s: productBrandName = "",
      [`priceList.${persona}.price_d`]: productPrice = 0,
      ["productImages.labelWithUrl_ss"]: labledImages,
    } = requiredItemsSkuData[0] ?? {}

    if (!requiredSkuList.length) {
      const {
        apiEndPointData: {
          map: {
            targetEnvironmentId: environmentId = "",
            targetPropertyId: propertyToken = "",
            targetMbox: mboxName = "",
          },
        } = {},
      } = config ?? {}

      const payload = {
        id: {
          tntId: getTntId(),
        },
        environmentId: environmentId,
        context: {
          channel: "web",
          address: {
            url: window?.location?.href ?? "",
          },
        },
        property: {
          token: propertyToken,
        },
        execute: {
          mboxes: [
            {
              index: 1,
              name: mboxName,
              product: {
                id: "K-" + updatedSku,
              },
            },
          ],
        },
      }
      try {
        const { options: [content] = [] } =
          await getListOfRequiredAdobeTargetSkus(payload, logger)
        const { content: data = {} } = content
        const {
          adobeRecommendations: {
            recommendedItems = "",
            activityDetails = {},
          } = {},
        } = JSON.parse(data)
        setCriteriaTitle(activityDetails["criteria.title"])
        recommendedItems?.split(",")?.map(item => {
          if (item !== "") {
            requiredSkuList.push(item.replace("K-", ""))
          }
        })
        if (!requiredSkuList.length) {
          setRequiredItemsProductData([])
          setLoading(false)
          return
        } else {
          setRecommendationSource("target")
        }
      } catch (error) {
        logger.error("Error fetching Adobe Target recommendations", {
          error,
          payload,
        })
        setRequiredItemsProductData([])
        setLoading(false)
        return
      }
    } else {
      setRecommendationSource("pim")
    }

    let imgId = ""
    if (labledImages.length) {
      const index = labledImages
        .map(item => (item.split("|")?.length ? item.split("|")[0] : ""))
        .indexOf(updatedSku)
      const assetId = labledImages[index]?.split("|")?.length
        ? labledImages[index].split("|")[1]
        : ""
      imgId = assetId
    }

    const productImageUrl = getPresetUrl(
      width,
      rowCols,
      baseUrl,
      imgId,
      parsedPresetConfigs
    )

    setParentProductData({
      customerFacingSKU: updatedSku,
      name: productBrandName,
      unitPrice: productPrice,
      availableQuantity: parentProductQuantity,
      productImageUrl,
    })

    setBrandName(siteName)

    if (requiredSkuList.length) {
      const { inventories = [] } = await getProductInventory(
        requiredSkuList.join(",")
      )

      const { data = {} } = await getProductDataFromSkuIds(
        persona,
        requiredSkuList,
        lwAppName
      )
      if (!_isEmpty(data)) {
        const { response: { docs: requiredSkusProductData = [] } = {} } = data
        const requiredItemsProductData = []
        if (requiredSkusProductData.length) {
          requiredSkusProductData.forEach(item => {
            const inventoryObj =
              inventories.find(
                inventoryItem => item.sku_s === inventoryItem.sku
              ) ?? {}

            const colors = item["Color.SKU.Details_ss"] ?? []
            let matchedColor = []
            if (colors.length) {
              matchedColor = colors.filter(colorItem => {
                const colorSplit = colorItem.split("|")
                const itemLen = colorSplit.length
                return (
                  colorSplit[itemLen - 1].substring(
                    2,
                    colorSplit[itemLen - 1].length
                  ) === item.sku_s
                )
              })
            }

            requiredItemsProductData.push({
              availableQuantity: inventoryObj?.availableQuantity ?? 0,
              productColor: matchedColor ? matchedColor[0]?.split("|")[0] : "",
              ...item,
            })
          })
          setLoading(false)
          makeDefaultSelection(
            requiredItemsProductData,
            requiredItemsSkuData[0],
            parentProductQuantity
          )
          setRequiredItemsProductData(requiredItemsProductData)
        }
      }
    }
  }

  const getSubTotal = (
    requiredItems = [],
    parentSku = "",
    skuId = "",
    qty = 1
  ) => {
    const addedItems = { ...includedItems }
    const availableParentSku = addedItems[parentSku]
    return requiredItems.reduce(
      (
        n,
        {
          [`priceList.${persona}.discountedPrice_d`]: discountedPrice = 0,
          [`priceList.${persona}.price_d`]: productPrice = 0,
          sku_s: sku = "",
          availableQuantity = 0,
        }
      ) => {
        let quantity = !_isEmpty(availableParentSku)
          ? availableParentSku.includedItems[sku]?.quantity
          : 1
        if (skuId && sku === skuId) {
          quantity = qty
        }
        const price = availableQuantity ? discountedPrice || productPrice : 0
        return n + price * quantity
      },
      0
    )
  }

  const makeDefaultSelection = (
    productData = [],
    masterSkuData = {},
    parentProductQuantity = 0
  ) => {
    const includedRequiredItems = {}
    const { [`priceList.${persona}.finalPrice_d`]: productFinalPrice = 0 } =
      masterSkuData
    const productPrice = parentProductQuantity > 0 ? productFinalPrice : 0
    includedRequiredItems[updatedSku] = {
      subTotal: getSubTotal(productData, updatedSku) + productPrice,
      includedItems: {
        ...productData.reduce(
          (obj, cur) => ({
            ...obj,
            [cur.sku_s]: {
              check: cur.availableQuantity > 0,
              quantity: 1,
              availableQuantity: cur.availableQuantity,
              isBundle: cur?.ctProductType_s === "engineer-bundle",
            },
          }),
          {}
        ),
      },
      check: parentProductQuantity > 0,
      isBundle: masterSkuData?.ctProductType_s === "engineer-bundle",
    }
    setIncludedItems(includedRequiredItems)
  }
  const curatedRecommendationTitle = criteriaTitle || mainTitle

  const checkChangeHandlerForParentProduct = (
    checked = false,
    parentSku = ""
  ) => {
    const { unitPrice = 0 } = parentProductData
    const addedItems = { ...includedItems }
    const availableParentSku = addedItems[parentSku]
    availableParentSku.check = checked
    const operand = checked ? "+" : "-"
    const { subTotal = 0 } = availableParentSku
    availableParentSku.subTotal = eval(subTotal + operand + unitPrice)
    setIncludedItems(addedItems)

    const parentProductDataInfo = PdpInfo.data
    const parentProductDataAll = {
      ProductDescriptionProductShort_s:
        parentProductDataInfo?.ProductDescriptionProductShort_s,
      Product_Category: parentProductDataInfo?.ProductLocalCategory_s,
      image: parentProductData?.productImageUrl,
      unitPrice: parentProductData?.unitPrice,
      customerFacingSKU: parentProductData?.customerFacingSKU,
      productTileName: parentProductData?.name,
      [`priceList.${persona}.finalPrice_d`]:
        PdpInfo.data?.[`priceList.${persona}.finalPrice_d`],
      [`priceList.${persona}.discountedPrice_d`]:
        PdpInfo.data?.[`priceList.${persona}.discountedPrice_d`],
      parentglobalPromotionPrice:
        PdpInfo.data?.[`priceList.${persona}.finalPrice_d`],
      CustomerFacingBrand_s: parentProductDataInfo?.CustomerFacingBrand_s,
      RegionBrandName_s: parentProductDataInfo?.RegionBrandName_s,
      availableQuantity: 1,
      qty: 1,
      sku_s: parentProductData.customerFacingSKU,
      productParentImage: parentProductData.productImageUrl,
      itemType: "product",
      productRoomType: parentProductDataInfo?.category1_s,
    }
    const includeTitle = authData?.includeTitle || curatedRecommendationTitle

    const itemLength =
      requiredItemsProductData.length > 0
        ? requiredItemsProductData.length - 1
        : "-1"

    const isParent = true
    addAnalyticsForIncludeforRequiredItem({
      checked,
      included: includeTitle,
      recommendationTitle,
      installServiceData: parentProductDataAll,
      index: 1,
      childindex: itemLength,
      persona,
      isParent,
      recommendationSource,
    })
  }

  const checkChangeHandlerForReqItems = (
    checked = false,
    parentSku = "",
    skuId = ""
  ) => {
    const addedItems = { ...includedItems }
    const availableParentSku = addedItems[parentSku]
    if (!_isEmpty(availableParentSku)) {
      availableParentSku.includedItems[skuId].check = checked
      const checkedSkus = Object.keys(availableParentSku.includedItems).filter(
        key => availableParentSku.includedItems[key]?.check
      )

      const requiredFilteredItems = requiredItemsProductData.filter(item =>
        checkedSkus.some(sku => item.sku_s === sku)
      )
      const { unitPrice = 0 } = parentProductData
      const productPrice = availableParentSku.check ? unitPrice : 0
      const subTotal = getSubTotal(requiredFilteredItems, parentSku)
      availableParentSku.subTotal = subTotal + productPrice

      setIncludedItems(addedItems)
    }
  }
  const addSelectedItems = async subTotal => {
    if (!_isEmpty(includedItems)) {
      setAddToCartLoader(true)
      const addedItems = { ...includedItems }
      const availableParentSku = addedItems[updatedSku]
      if (!_isEmpty(availableParentSku)) {
        const requiredItems = availableParentSku.includedItems
        const selectedItems = Object.keys(requiredItems)
          .filter(key => requiredItems[key].check)
          .map(item => ({
            sku: item,
            quantity: requiredItems[item]?.quantity,
            isBundle: requiredItems[item]?.isBundle,
          }))
        if (availableParentSku.check) {
          selectedItems.push({
            sku: updatedSku,
            quantity: 1,
            isBundle: availableParentSku?.isBundle ?? false,
          })
        }
        if (selectedItems.length) {
          const allSelectedItems = selectedItems.map(item => ({
            ...item,
            brand: brandName,
          }))
          addItemsToCart(allSelectedItems)

          if (allSelectedItems.length) {
            let selectedIncludePdpData = []
            let parentProductDataAll = []
            const parentProduct = allSelectedItems.filter(item => {
              return item.sku === PdpInfo.sku
            })

            if (parentProduct.length) {
              const parentProductDataInfo = PdpInfo.data ?? []
              const { ["Color.SKU.Details_ss"]: colors = [] } =
                parentProductDataInfo
              let matchedColor = []
              if (colors.length) {
                matchedColor = colors.filter(colorItem => {
                  const colorSplit = colorItem.split("|")
                  const itemLen = colorSplit.length
                  return (
                    colorSplit[itemLen - 1].substring(
                      2,
                      colorSplit[itemLen - 1].length
                    ) === PdpInfo.sku
                  )
                })
              }
              parentProductDataAll = {
                desc: parentProductDataInfo?.ProductDescriptionProductShort_s,
                category: parentProductDataInfo?.ProductLocalCategory_s,
                image: parentProductData?.productImageUrl,
                unitPrice: parentProductData?.unitPrice,
                customerFacingSKU: parentProductData?.customerFacingSKU,
                productTileName: parentProductData?.name,
                quantity: 1,
                name: parentProductData?.name,
                color: matchedColor ? matchedColor[0]?.split("|")[0] : "",
              }
            }
            const childRecommendationProduct = allSelectedItems?.filter(
              item => item.sku !== PdpInfo.sku
            )
            selectedIncludePdpData = childRecommendationProduct?.map(
              childItem => {
                let foundIndex = -1
                const item =
                  requiredItemsProductData.find((requiredItem, index) => {
                    foundIndex = index
                    return requiredItem.sku_s === childItem.sku
                  }) ?? {}
                return { ...item, foundIndex }
              }
            )
            selectedIncludePdpData.sort(
              (a, b) => Number(a.foundIndex) - Number(b.foundIndex)
            )
            const productDataForAnlytics = {
              parentProductDataAll,
              selectedIncludePdpData,
            }
            addAnalyticsForPdpAddToCart(
              subTotal,
              productDataForAnlytics,
              persona,
              recommendationSource
            )
          }
        }
      }
    }
  }

  const addItemsToCart = async items => {
    const payload = await addToCartItem([...items])
    const body = {
      actions: payload,
    }

    dispatch(addToCart(body))
      .unwrap()
      .then(res => {
        dispatch(showPreviewCart())
        setAddToCartLoader(false)
        handleAddtoCartResponse(res)
      })
      .catch(error => {
        logger.error("Error adding curated products to cart", { error, body })
      })
  }

  const handleAddtoCartResponse = ({ payload }) => {
    if (payload) {
      dispatch(setCart(payload))
    }
  }

  const getSelectedItemNum = (item = {}) => {
    const { includedItems = {}, check = false } = item
    let num = 0
    for (const key in includedItems) {
      if (Object.prototype.hasOwnProperty.call(includedItems, key)) {
        if (includedItems[key]?.check) num += 1
      }
    }
    if (check) num = num + 1
    return num
  }

  if (!loading && requiredItemsProductData.length) {
    const subTotal = !_isEmpty(includedItems)
      ? includedItems[updatedSku]?.subTotal
      : 0
    const selectedIncludesNum = !_isEmpty(includedItems)
      ? getSelectedItemNum(includedItems[updatedSku])
      : 0

    return (
      <div className={styles.pdpcuratedproductmodule}>
        <div className="pdp-curated-recommendation">
          <h2 className="pdp-curated-recommendation__title">
            {curatedRecommendationTitle}
          </h2>
          <div className="pdp-curated-recommendation__conatiner">
            <ParentProductCard
              containerClassName="-pdp-required-item"
              checkChangeHandlerForParentProduct={
                checkChangeHandlerForParentProduct
              }
              includedItems={includedItems}
              productTitleUpSell={productTitle}
              {...parentProductData}
              {...authData}
            />
            <div className="right-side-conatiner">
              <div className="header">
                <h3 className="title">{recommendationTitle}</h3>
                <div className="button-total">
                  <div className="total">
                    {subTotal > 0 && subTotal !== "0.00" ? (
                      <DisplayDiscountPrice price={subTotal} />
                    ) : null}
                  </div>
                  <div className="add-to-cart-button">
                    <Button
                      role="button"
                      label={`Add to Cart (${selectedIncludesNum})`}
                      size="large"
                      type="primary"
                      flexible={true}
                      disabled={!subTotal || subTotal === "0.00"}
                      loading={addToCartLoader}
                      onClick={() => addSelectedItems(subTotal)}
                    />
                  </div>
                </div>
              </div>
              <div className="pdp-required-modal-right-side">
                <div
                  className={`pdp-required-modal-container-list ${
                    requiredItemsProductData.length <= 1
                      ? "required-item-single"
                      : ""
                  }`}
                >
                  {requiredItemsProductData.map((item, upSellDataIndex) => (
                    <React.Fragment key={item.sku_s}>
                      <RequiredProductItem
                        dynamicClass="-pdp"
                        requiredProductItem={item}
                        persona={persona}
                        showQtyInput={false}
                        includedItems={includedItems}
                        parentSku={updatedSku}
                        updateRequiredItems={checkChangeHandlerForReqItems}
                        baseUrl={baseUrl}
                        parsedConfigs={parsedPresetConfigs}
                        upSellDataIndex="0"
                        recommendationSource={recommendationSource}
                        index={upSellDataIndex}
                        {...authData}
                        productData={item}
                        quickViewAuthData={quickViewAuthData}
                        openQuickViewModal={openQuickViewModal}
                        isPdp={true}
                      ></RequiredProductItem>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="subtotal-add-to-cart--mobile">
            <div className="number-total-mobile">
              <div className="selected-mobile">
                {selectedTitle} ({selectedIncludesNum})
              </div>
              <div className="total-price">
                {subTotal > 0 && subTotal !== "0.00" ? (
                  <DisplayDiscountPrice price={subTotal} />
                ) : null}
              </div>
            </div>
            <div className="add-to-cart-">
              <Button
                role="button"
                label={"Add to Cart"}
                size="large"
                type="primary"
                flexible={true}
                loading={addToCartLoader}
                disabled={!subTotal || subTotal === "0.00"}
                onClick={() => addSelectedItems(subTotal)}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
  return null
}

export default PDPCuratedProducts
